import 'swiper/css';
import 'swiper/css/pagination';

import Grid from '@mui/material/Grid';
import { clsx } from 'clsx';
import { useId, useRef, useState } from 'react';
import { Navigation } from 'swiper/modules';
import { Swiper, type SwiperClass, SwiperSlide } from 'swiper/react';

import Eyebrow from '@/components/Eyebrow/Eyebrow';
import Icon, { IconName } from '@/components/Icon/Icon';
import VideoPlayer from '@/components/VideoPlayer/VideoPlayer';
import type { FrontifyImageContent } from '@/contentful/contentful-exports';
import useViewPortSize from '@/hooks/useViewportSize';
import { brandColors } from '@/styles/theme/values/brandColors';
import type { PlaylistVideoCardCf } from '@/types';
import { getFrontifyBackgroundImageUrl } from '@/utils/frontify';

import useStyles from './Playlist.styles';
import { PlaylistVideoCard } from './PlaylistVideoCard';

export const Playlist = ({
  videos,
  isOnDark,
}: {
  isOnDark: boolean;
  videos: PlaylistVideoCardCf[];
}) => {
  const { classes } = useStyles({ isOnDark });
  const [isPlaying, setIsPlaying] = useState(false);
  const [showThumbnail, setShowThumbnail] = useState(true);
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  const activeVideo = videos[activeVideoIndex] || videos[0];
  const [thumbnail] = activeVideo.fields.thumbnail as unknown as [
    FrontifyImageContent,
  ];
  const swiperRef = useRef<SwiperClass>(null);
  const prevElId = useId().replaceAll(':', '');
  const nextElId = useId().replaceAll(':', '');
  const [isMuted, setIsMuted] = useState(false);
  const { isMobile } = useViewPortSize();

  const handlePlaybackStart = (index: number) => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (activeVideoIndex !== index) {
      if (isIOS) setIsMuted(true);
      setActiveVideoIndex(index);
      setIsPlaying(true);
      swiperRef.current?.slideTo(index);
    }

    if (activeVideoIndex === index) {
      setIsPlaying((prev) => !prev);
    }

    setShowThumbnail(false);
  };

  const handleVideoEnd = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (activeVideoIndex + 1 < videos.length) {
      if (isIOS) setIsMuted(true);
      setActiveVideoIndex(activeVideoIndex + 1);
      setIsPlaying(true);
      swiperRef.current?.slideTo(activeVideoIndex);
    }

    if (activeVideoIndex + 1 === videos.length) {
      setIsPlaying(false);
      setActiveVideoIndex(0);
      setIsMuted(false);
      setShowThumbnail(true);
      swiperRef.current?.slideTo(0);
    }
  };

  return (
    <div className={classes.playlistContainer}>
      <Grid className={classes.grid} size={12}>
        <div className={classes.videoContainer}>
          {/** biome-ignore lint/a11y/useSemanticElements: Automated migration to Biome */}
          <div
            className={classes.playerOverlay}
            role='button'
            tabIndex={0}
            aria-label={`Play ${activeVideo.fields.headline} video`}
            style={{
              backgroundImage: getFrontifyBackgroundImageUrl(
                thumbnail.preview_url,
                isMobile ? 768 : 1440
              ),
              zIndex: showThumbnail ? 1 : -1,
            }}
            onClick={() => handlePlaybackStart(0)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handlePlaybackStart(0);
            }}
          >
            <Icon name={IconName.PLAY_SQUARE} width={80} />
          </div>
          {/** biome-ignore lint/correctness/useUniqueElementIds: Automated migration to Biome */}
          <VideoPlayer
            key={activeVideoIndex}
            id='playlist-vimeo-player'
            data-testid='playlist-video-player'
            className={classes.videoPlayer}
            url={activeVideo.fields.vimeoUrl}
            playing={isPlaying}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={handleVideoEnd}
            controls
            muted={isMuted}
            playsinline
            width={'100%'}
            height={'100%'}
            config={{
              playerOptions: { color: brandColors.chargeBlue.substring(1) },
            }}
          />
        </div>

        <div className={classes.eyebrowWrapper}>
          <Eyebrow text='PLAYLIST' isOnDark={isOnDark} />
        </div>

        <Swiper
          navigation={{
            prevEl: `#${prevElId}`,
            nextEl: `#${nextElId}`,
            disabledClass: classes.navigationButtonDisabled,
          }}
          modules={[Navigation]}
          slidesPerView={'auto'}
          onSwiper={(swiper: SwiperClass) => {
            swiperRef.current = swiper;
          }}
        >
          {videos.map((card, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
            <SwiperSlide className={classes.swiperSlide} key={index}>
              <PlaylistVideoCard
                isOnDark={isOnDark}
                key={card.sys.id}
                card={card}
                position={index + 1}
                length={videos.length}
                isPlaying={isPlaying && index === activeVideoIndex}
                onThumbnailClick={() => handlePlaybackStart(index)}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </Grid>
      <div
        className={clsx(classes.navigation, {
          [classes.navigationHidden]:
            !swiperRef.current?.allowSlideNext &&
            !swiperRef.current?.allowSlidePrev,
        })}
      >
        <button
          id={prevElId}
          type='button'
          aria-label='Previous video'
          className={classes.navigationButton}
        >
          <Icon name={IconName.ARROW_LEFT} width={24} />
        </button>
        <button
          id={nextElId}
          type='button'
          aria-label='Next video'
          className={classes.navigationButton}
        >
          <Icon name={IconName.ARROW_RIGHT} width={24} />
        </button>
      </div>
    </div>
  );
};
